import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Camera, Trash, X, UploadSimple, SpinnerGap, Image as ImageIcon } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function PhotoGallery({ jobId }) {
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();

  const load = () => api.listJobPhotos(jobId).then(setPhotos).catch(() => {});
  useEffect(() => { load(); }, [jobId]); // eslint-disable-line

  const uploadFiles = async (files) => {
    const arr = Array.from(files).filter((f) => f.type.startsWith("image/"));
    if (!arr.length) return;
    setUploading(true);
    try {
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        await api.uploadJobPhoto(jobId, fd);
      }
      toast.success(`${arr.length} photo${arr.length > 1 ? "s" : ""} uploaded`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const onDrop = (e) => { e.preventDefault(); setDrag(false); uploadFiles(e.dataTransfer.files); };
  const remove = async (p) => { if (!window.confirm("Remove this photo?")) return; await api.deletePhoto(p.id); toast.success("Photo removed"); load(); };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-slate-700"><Camera size={18} weight="duotone" className="text-[#00B4D8]" /><h3 className="font-display font-semibold">Job Photos</h3><span className="text-xs text-slate-400">({photos.length})</span></div>
        <button onClick={() => inputRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#00B4D8] hover:text-cyan-600 transition-colors duration-200" data-testid="upload-photo-trigger">
          {uploading ? <SpinnerGap size={16} className="animate-spin" /> : <UploadSimple size={16} />} Upload
        </button>
        <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" data-testid="photo-file-input" onChange={(e) => uploadFiles(e.target.files)} />
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        onClick={() => photos.length === 0 && inputRef.current?.click()}
        className={`rounded-lg border-2 border-dashed transition-colors duration-200 ${drag ? "border-[#00B4D8] bg-cyan-50" : "border-slate-200"} ${photos.length === 0 ? "p-10 cursor-pointer" : "p-3"}`}
        data-testid="photo-dropzone"
      >
        {photos.length === 0 ? (
          <div className="text-center text-slate-400">
            <ImageIcon size={40} weight="duotone" className="mx-auto mb-2 text-slate-300" />
            <p className="text-sm font-medium">Drag & drop photos here, or click to browse</p>
            <p className="text-xs mt-1">Before / after shots, damage, completed work — they'll be ready for your website gallery.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="relative group aspect-square rounded-md overflow-hidden bg-slate-100" data-testid={`photo-${p.id}`}>
                <img src={api.photoUrl(p.url)} alt={p.original_filename} className="w-full h-full object-cover cursor-pointer" onClick={() => setLightbox(p)} loading="lazy" />
                <button onClick={() => remove(p)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-rose-500" data-testid={`delete-photo-${p.id}`}><Trash size={14} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6" onClick={() => setLightbox(null)} data-testid="photo-lightbox">
          <button className="absolute top-5 right-5 text-white/80 hover:text-white"><X size={30} /></button>
          <img src={api.photoUrl(lightbox.url)} alt={lightbox.original_filename} className="max-h-[90vh] max-w-full rounded-lg object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
